
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useInvoiceUpload } from "@/hooks/useInvoiceUpload";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface InvoiceUploadProps {
  onVinDetected: (vin: string) => void;
  isLoading: boolean;
  vinLookupId?: string;
}

export const InvoiceUpload = ({ onVinDetected, isLoading, vinLookupId }: InvoiceUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { handleInvoiceProcess } = useInvoiceUpload({ onVinDetected });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      console.log("No file selected");
      return;
    }

    console.log("File selected:", file.name, file.type, file.size);
    setUploading(true);
    setUploadProgress(10); // Start progress
    
    try {
      setUploadProgress(30); // File preparation complete
      
      const result = await handleInvoiceProcess(file);
      console.log("Upload result:", result);

      setUploadProgress(70); // Processing complete
      
      if (result?.success) {
        toast.success("Document processed successfully");
      } else {
        throw new Error(result?.error || "Failed to process document");
      }
      
      setUploadProgress(100); // All done
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to upload document. Please try again.");
    } finally {
      // Reset the file input and states after a short delay
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }, 1000);
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={handleFileChange}
        ref={fileInputRef}
        disabled={uploading || isLoading}
      />
      
      <Button
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading || isLoading}
        className="w-full relative"
      >
        {uploading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-everest-teal"></div>
            Uploading...
          </div>
        ) : (
          "Upload Repair Estimate (PDF)"
        )}
      </Button>

      {uploading && (
        <div className="space-y-2">
          <Progress value={uploadProgress} />
          <p className="text-sm text-muted-foreground text-center">
            {uploadProgress < 30 && "Preparing file..."}
            {uploadProgress >= 30 && uploadProgress < 70 && "Processing document..."}
            {uploadProgress >= 70 && uploadProgress < 100 && "Finalizing..."}
            {uploadProgress === 100 && "Complete!"}
          </p>
        </div>
      )}
      
      <div className="text-sm text-muted-foreground text-center">
        Upload a PDF repair estimate containing a VIN number
      </div>
    </div>
  );
};
